/**
 * What a room-check item and step carry beyond their words (owner, 2026-09-29, walking room checks):
 *
 *  - "What's this?" (the mop sink: "where is that sink? Do all restaurants have that?"): every item's info card, with
 *    What it is, Where it usually is, Why (its basis and citation) and, when the rule depends on the business type,
 *    Who must have it. ROOM_INFO below; unsourced lines are plain descriptions, never rules.
 *  - Placement ("Where is your mop sink?"): a fixture that lives in one room, asked once. It shows only where the rule
 *    applies (MOP_SINK_RULE): a food establishment yes; a home kitchen permit (MEHK) no; a food truck no (the service
 *    sink is the commissary's, R392-102-3(4)(f)(iii)); a home no.
 *  - Inline step actions ("Log what you threw out… why can't I do that right now?"): a step that asks you to record or act
 *    elsewhere carries its action (stepAction), with the paper log that holds the same record, if one exists.
 *  - Learn more ("We have a full library resource on this"): a step's library topic (libraryFor) and, for the fridge
 *    order, one step with a diagram (FRIDGE_ORDER), never split into many.
 *
 * Gates (test/roomextras.test.ts): I1 every item has an info card whose lines pass lintEasy's length, word and bracket
 * rules; I2 the mop sink hides for a MEHK, a truck and a home (control: a restaurant shows it); I3 "We don't have one"
 * gives the rule notice with its citation; A1 actions only from the step's own words; D1 the fridge tiers follow library
 * fridge-storage-order and cite Food Code 3-302.11, each example in exactly one tier.
 */
import type { EasyIcon } from "./easy.ts";
import { lintEasy } from "./easy.ts";
import { LIBRARY } from "./library.ts";
import type { PermitType } from "./model.ts";
import type { ZonePreset } from "./objects.ts";
import { ROOMEXTRAS_UT } from "./states/ut/roomextras.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { perState, wordsFor } from "./states/words.ts";

const WORDS: Record<UsState, StateWords<typeof ROOMEXTRAS_UT>> = perStateTable("ROOMEXTRAS", ROOMEXTRAS_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/* ─────────────── info cards ─────────────── */

export interface ItemInfo {
  /** One line: what the thing is. */
  what: string;
  /** Where it usually is. */
  where: string;
  /** Why it's on the list: the rule or the reason, in plain words. */
  why: string;
  /** "Required by law · Food Code 5-203.13", "Suggested", "Preference, not a rule." */
  basisLine: string;
  citation?: string;
  /** When the rule depends on the kind of place: who must have it. */
  who?: string;
}

type InfoText = { what: string; where: string; who?: string };

/** Business surfaces (surfaces.ts ids). Descriptions only: every rule comes from the item's own basis. */
export const SURFACE_INFO: Record<string, InfoText> = {
  "espresso-group-heads": { what: "The part of the espresso machine that holds the coffee.", where: "On the front of the espresso machine." },
  "steam-wand": { what: "The metal tube that steams milk.", where: "On the side of the espresso machine." },
  "espresso-descale": { what: "Removing hard water scale from the espresso machine.", where: "Inside the espresso machine and its water filter." },
  "espresso-exterior": { what: "The outside, the drip tray and the knock box.", where: "Around the espresso machine on the bar." },
  "coffee-grinder": { what: "The bean hopper and the blades that grind coffee.", where: "On the bar, next to the espresso machine." },
  "milk-pitchers": { what: "Small steel pitchers for steaming milk.", where: "On the bar, near the steam wand." },
  "drip-brewer": { what: "The machine or urn that brews batch coffee.", where: "On the bar or the front counter." },
  "cold-brew-containers": { what: "Big containers and filters for cold brew.", where: "In the fridge or on the bar." },
  "syrup-pumps": { what: "Pumps and bottles for flavor syrups.", where: "On the bar, near the drinks." },
  "blender-jars": { what: "The jars of the drink blenders.", where: "On the bar." },
  "ice-machine": { what: "The machine that makes ice, and its bin.", where: "Behind the bar or in the back." },
  "ice-scoop": { what: "The scoops used for ice.", where: "In a holder next to the ice bin." },
  "beverage-nozzles": { what: "The spouts on soda and drink dispensers.", where: "On the drink machine." },
  "prep-counter": { what: "Counters and tables where food is made.", where: "In the kitchen or behind the counter." },
  "cutting-board-plastic": { what: "Color-coded plastic boards for cutting food.", where: "At the prep counter." },
  "cutting-board-wood": { what: "Wood boards and butcher blocks.", where: "At the prep counter." },
  "knives-utensils": { what: "Knives, spatulas and other kitchen tools.", where: "At the prep counter and the line." },
  tongs: { what: "Tongs and serving tools.", where: "On the line and at serving stations." },
  "thermometer-probe": { what: "The food thermometer's metal tip.", where: "In a pocket or a drawer at the line." },
  slicer: { what: "The machine that slices meat and cheese.", where: "On a prep counter." },
  mixer: { what: "The stand mixer, its bowl and its parts.", where: "On a prep counter or a shelf." },
  "can-opener": { what: "The opener for cans.", where: "Mounted on a prep table, or in a drawer." },
  "pans-containers": { what: "Pots, pans and food containers.", where: "On shelves near the line and the dish area." },
  "prep-sink": { what: "A sink used only for washing food.", where: "In the kitchen, apart from the hand sink." },
  "hand-sink": { what: "A sink used only for washing hands.", where: "In the kitchen and at the bar, easy to reach.", who: "Every food business must have one where food is made." },
  "range-stovetop": { what: "The burners where food is cooked.", where: "On the cook line, under the hood." },
  griddle: { what: "The flat hot top for cooking.", where: "On the cook line." },
  fryer: { what: "The deep fryer and its oil.", where: "On the cook line, under the hood." },
  oven: { what: "The ovens.", where: "On the cook line or in the kitchen." },
  microwave: { what: "The microwave.", where: "On a counter or a shelf." },
  "hood-filters": { what: "The metal filters in the hood above the stove.", where: "Above the cook line." },
  "grill-grates": { what: "The bars of an outdoor grill.", where: "On the grill outside." },
  "three-compartment-sink": { what: "A sink with three tubs: wash, rinse, sanitize.", where: "In the dish area.", who: "Needed for washing dishes by hand, unless a dish machine does it." },
  dishwasher: { what: "The dish machine.", where: "In the dish area." },
  "wiping-cloth-bucket": { what: "Buckets of sanitizer that hold wiping cloths.", where: "At the prep counter and the bar." },
  "reach-in-fridge": { what: "A fridge you reach into.", where: "Under or next to the counter, or on the line." },
  "walk-in": { what: "A cold room you walk into.", where: "In the back of the kitchen." },
  freezer: { what: "The freezer.", where: "In the kitchen or the back." },
  "door-gaskets": { what: "The rubber seals around fridge and freezer doors.", where: "On the edge of each fridge and freezer door." },
  "coolers-ice-chests": { what: "Coolers and ice chests for food.", where: "In storage, or out at events." },
  "storage-shelves": { what: "The shelves that hold food and supplies.", where: "In dry storage and the walk-in." },
  "dry-storage-bins": { what: "Bins that hold flour, sugar and other dry food.", where: "In dry storage, under or on shelves." },
  "pastry-display-case": { what: "The glass case and trays that show pastries.", where: "At the front counter." },
  "condiment-station": { what: "The self-serve spot for milk, sugar and sauces.", where: "Near the front counter or the seating." },
  "dining-tables": { what: "The tables where customers eat.", where: "In the seating area." },
  chairs: { what: "The chairs where customers sit.", where: "In the seating area." },
  "high-chairs": { what: "High chairs and booster seats for children.", where: "Near the seating area." },
  menus: { what: "Menus and table cards.", where: "At the counter and on tables." },
  "payment-screen": { what: "The screen or card reader for paying.", where: "At the front counter." },
  "door-handles": { what: "Handles, light switches and faucet handles.", where: "On doors, walls and sinks everywhere." },
  "windows-glass": { what: "Windows, glass doors and mirrors.", where: "At the front and in the restrooms." },
  "restroom-toilets": { what: "The toilets and urinals.", where: "In the restroom." },
  "restroom-sink": { what: "The restroom's hand sink, soap and towels.", where: "In the restroom, near the toilet." },
  "restroom-floor": { what: "The restroom floor.", where: "In the restroom." },
  "kitchen-floor": { what: "The kitchen floor.", where: "In the kitchen and the back." },
  "dining-floor": { what: "The floor where customers walk.", where: "In the seating area." },
  "hardwood-floor": { what: "Wood floors.", where: "In the seating area." },
  "floor-drains": { what: "The metal grates in the floor that take water away.", where: "In the kitchen and dish area floor." },
  "walls-ceilings": { what: "Walls, ceilings, lights and vents.", where: "Above and around every room." },
  "mop-sink": {
    what: "A low, deep sink just for mop water.", where: "Low with a floor drain, often in the back or a closet.",
    who: "Food businesses: yes. Home kitchen permits: no. Food trucks: at the commissary.",
  },
  "trash-cans-indoor": { what: "The trash cans inside.", where: "In the kitchen, the restroom and the seating area." },
  "trash-cans-outdoor": { what: "Trash and recycling bins outside.", where: "By the doors or out back." },
  "dumpster-area": { what: "The dumpster and the ground around it.", where: "Out back." },
  "grease-trap": { what: "The box that catches grease, and used fryer oil.", where: "Under the dish sink, or outside." },
};

/** Home items (roomcheck.ts HOME_ITEM_SPECS keys) and business put-away items. */
export const KEY_INFO: Record<string, InfoText> = {
  "req:fc-cleaning-tools": { what: "Mops, buckets and cleaning cloths.", where: "Hung up near the mop sink, apart from food." },
  "req:fc-chemicals": { what: "Cleaners, sanitizer and other chemicals.", where: "On a low shelf of their own, away from food." },
  "req:fc-pest-control": { what: "Signs of mice, rats or bugs.", where: "Along walls, under equipment and in corners." },
  "home:dusting": { what: "Shelves and the tops of furniture.", where: "Around the room, high to low." },
  "home:mirror": { what: "The mirror over the sink.", where: "Above the bathroom sink." },
  "home:counters": { what: "The kitchen counters.", where: "Where you cook and make food." },
  "home:stovetop": { what: "The stove's cooking top.", where: "In the kitchen." },
  "home:microwave": { what: "The microwave, inside and out.", where: "On the counter or over the stove." },
  "home:fridge": { what: "The fridge inside: old food, spills, shelves and drawers.", where: "Inside the fridge." },
  "home:dishwasher": { what: "The dishwasher: loading it and running it with hot water.", where: "In the kitchen." },
  "home:fridge-temp": { what: "How cold the fridge is.", where: "A thermometer on a fridge shelf." },
  "home:switches": { what: "Light switches, door handles and knobs.", where: "On the walls and doors." },
  "home:bath-sink": { what: "The bathroom sink and counter.", where: "In the bathroom." },
  "home:sheets": { what: "The bed sheets and pillowcases.", where: "On the bed." },
  "home:sponge": { what: "The dish sponge or brush.", where: "By the kitchen sink." },
  "home:shower": { what: "The shower and tub.", where: "In the bathroom." },
  "home:toilet": { what: "The toilet: seat, bowl and handle.", where: "In the bathroom." },
  "home:trash": { what: "The trash can and its bag.", where: "Under the sink or in a corner." },
  "home:floor": { what: "The floor.", where: "Across the whole room." },
  "home:bath-floor": { what: "The bathroom floor.", where: "In the bathroom, most of all by the toilet." },
  "home:dish-towels": { what: "Towels for drying hands and dishes.", where: "Hung by the kitchen sink." },
  "home:hand-towels": { what: "The towel you dry your hands on.", where: "Hung by the bathroom sink." },
  "home:bed": { what: "A made bed.", where: "In the bedroom." },
  "home:clothes": { what: "Clean clothes in drawers or on hangers.", where: "In the closet and the dresser." },
  "home:bath-tools": { what: "The toilet brush, bathroom mop and cloths.", where: "Kept in the bathroom." },
  "home:cleaners": { what: "Cleaning sprays, bleach and pods.", where: "In their own cabinet, away from food." },
  "home:things-away": { what: "Things left out of their homes.", where: "On tables, chairs and the floor." },
  "home:sink-empty": { what: "The kitchen sink, with no dirty dishes in it.", where: "In the kitchen." },
  "home:food-away": { what: "Food left out on counters and tables.", where: "On the counters and the table." },
  "home:pests": { what: "Signs of mice or bugs.", where: "Under the sink, in cupboards and along walls." },
  "home:towels-hung": { what: "Bath towels, hung up to dry.", where: "On a hook or bar in the bathroom." },
  "home:tp-spare": { what: "A spare roll of toilet paper.", where: "Near the toilet, where you can reach it." },
  "home:clothes-floor": { what: "Clothes, shoes and bags left on the floor.", where: "On the floor, where people walk." },
  "home:hamper": { what: "The basket for dirty clothes.", where: "In the bedroom, closet or bathroom." },
  "home:nightstand": { what: "The small table by the bed.", where: "Next to the bed." },
  "home:laundry-soap": { what: "Detergent, pods and bleach.", where: "On a high shelf by the washer." },
  "home:washer": { what: "The washing machine.", where: "In the laundry room or closet." },
  "home:laundry-done": { what: "Clean laundry, taken out and folded.", where: "In the washer, the dryer or a basket." },
  "home:entry-clear": { what: "Shoes, bags and coats by the door.", where: "At the front door." },
  "home:toys-away": { what: "Toys put back in their bins.", where: "In the kids' room." },
  "home:desk-clear": { what: "The desk top, clear for work.", where: "In the office or at the desk." },
  "home:mail": { what: "Mail and papers, sorted.", where: "On the desk or by the door." },
};

/** Info lines pass lintEasy's length, word and bracket rules; they describe, so no verb-first rule (I1). */
export function lintInfo(line: string): string[] {
  return lintEasy(line).filter((i) => i.rule !== "E3").map((i) => i.message);
}

/* ─────────────── the mop sink: where it applies, where it is ─────────────── */

/** Surfaces placed in one room by the place's own answer ("Where is your mop sink?"). */
export const PLACEABLE = ["mop-sink"] as const;
export type Placeable = (typeof PLACEABLE)[number];
/** surfaceId → the room key it's in, or "none". */
export type Placements = Partial<Record<string, string>>;
/** A RoomSet row keeps the answers: room "placements", added ["mop-sink=storage"] (no new table). */
export const PLACEMENTS_ROOM = "placements";

const buildMopSinkRule = (state: UsState) => ({
  citation: "Food Code 5-203.13",
  /** 5-203.13(A), in plain words. */
  rule: "The Food Code requires a mop sink or a curbed cleaning spot with a floor drain.",
  none: "The Food Code requires one. Ask your health department.",
  truck: words(state).mopSinkTruck ?? "",
  mehk: words(state).mopSinkMehk ?? "",
} as const);
/** The mop sink rule for a state (a null slot leaves that line empty). Utah's is MOP_SINK_RULE. */
export const mopSinkRuleFor = perState(buildMopSinkRule);
export const MOP_SINK_RULE = mopSinkRuleFor("UT");

/** Does the mop sink belong in this place's room checks? A food truck's is at its commissary; a MEHK needs none. */
export function mopSinkApplies(p: { preset?: ZonePreset; permitType?: PermitType | string }): boolean {
  if (p.permitType === "mehk" || p.permitType === "mobile_food_business") return false;
  if (p.preset === "truck") return false;
  return true;
}

export const placementsFrom = (added: readonly string[] | undefined): Placements =>
  Object.fromEntries((added ?? []).map((a) => a.split("=")).filter((x) => x.length === 2 && x[0] && x[1])) as Placements;
export const placementsTo = (p: Placements): string[] => Object.entries(p).filter(([, v]) => !!v).map(([k, v]) => `${k}=${v}`);

export interface PlaceQuestion {
  surfaceId: Placeable;
  ask: string;
  rooms: { key: string; name: string }[];
  /** The room key, or "none". */
  answer?: string;
  /** "We don't have one" at a food business: the rule, plainly, with its citation. */
  notice?: { line: string; citation: string };
  who: string;
}

export function placeQuestions(p: { preset?: ZonePreset; permitType?: PermitType | string; placements: Placements; rooms: { key: string; name: string }[]; objects?: readonly { zone: string; surfaceId: string }[] }): PlaceQuestion[] {
  if (!mopSinkApplies(p)) return [];
  // Unanswered, but the place already has its own mop sink object: that object's room is the answer (still changeable).
  const own = p.objects?.find((o) => o.surfaceId === "mop-sink");
  const ownRoom = own ? p.rooms.find((r) => r.name.trim().toLowerCase() === own.zone.trim().toLowerCase())?.key : undefined;
  const answer = p.placements["mop-sink"] ?? ownRoom;
  return [{
    surfaceId: "mop-sink", ask: "Where is your mop sink?", rooms: p.rooms, ...(answer ? { answer } : {}),
    ...(answer === "none" ? { notice: { line: MOP_SINK_RULE.none, citation: MOP_SINK_RULE.citation } } : {}),
    who: SURFACE_INFO["mop-sink"]!.who!,
  }];
}

/* ─────────────── inline step actions ─────────────── */

export type InlineActionKind = "storage_order" | "dry_storage" | "discard" | "report_pest" | "report_mess" | "order" | "label_food" | "log_reading";
export interface InlineStepAction {
  kind: InlineActionKind;
  /** The secondary button: "Log what you threw out". */
  label: string;
  /** The paper log that holds the same record (templates.ts id), if one exists. Never invented. */
  templateId?: string;
  prefill?: Record<string, string>;
}

const ACTIONS: { re: RegExp; action: InlineStepAction }[] = [
  // The restock step ("Put food back in fridge order."): the check itself, recorded with who and when (core storageorder.ts).
  { re: /\bfridge order\b/i, action: { kind: "storage_order", label: "Check the order now", templateId: "storage_order" } },
  // The storage-shelves step that states the rule ("Keep the bottom shelf 6 inches off the floor, a few inches from the walls."): the dry-storage check itself (core drystorage.ts).
  { re: /\bbottom shelf 6 inches off the floor\b/i, action: { kind: "dry_storage", label: "Check dry storage", templateId: "dry_storage" } },
  { re: /\blog what you threw out\b|\bthrow out\b.*\(log it\)|\blog it\b.*\bthrow|\bthrew out\b.*\blog\b/i, action: { kind: "discard", label: "Log what you threw out", templateId: "discard" } },
  { re: /\b(report|tell).*\b(pest|droppings|gnaw)|\bpest report\b/i, action: { kind: "report_pest", label: "Report it", prefill: { type: "pest" } } },
  { re: /\breport it\b/i, action: { kind: "report_mess", label: "Report it", prefill: { type: "other" } } },
  { re: /\border more\b/i, action: { kind: "order", label: "Order more" } },
  { re: /\blabel it\b|\blabel (the|each) (food|container)/i, action: { kind: "label_food", label: "Label it" } },
  { re: /\bwrite down the (reading|temperature)\b|\blog the temp/i, action: { kind: "log_reading", label: "Write the reading", templateId: "cold_units" } },
];

/** A step's inline action, from its own words (the sourced line or its plain re-saying) (A1). */
export function stepAction(...texts: (string | undefined)[]): InlineStepAction | undefined {
  for (const t of texts) {
    if (!t) continue;
    const hit = ACTIONS.find((a) => a.re.test(t));
    if (hit) return { ...hit.action, ...(hit.action.prefill ? { prefill: { ...hit.action.prefill } } : {}) };
  }
  return undefined;
}

/**
 * "Which paper form should I use, if any?" (owner: "There should be an associated QR form and if not, print it and stick
 * it."). An action that writes a record names its printed sheet (templates.ts, the Paper feature's QR sheets): the one
 * already printed for this place ("Printed Tue"), or "Print it and stick it up" with a spot from the step's room. An
 * action with no template shows nothing: we never invent a form.
 */
export interface PaperLine {
  templateId: string;
  /** "On paper? Use the Discard log sheet." */
  line: string;
  /** A current printed sheet for this place: "Printed Tue." (its QR scans back into records). */
  printed?: { sheetId: string; code: string; line: string };
  /** None printed: offer to print it, and where to stick it. */
  print?: { label: "Print it and stick it up"; spot: string };
}

export function paperFor(
  action: Pick<InlineStepAction, "templateId"> | undefined,
  o: { sheets: readonly { id: string; code: string; templateId: string; periodEnd: string; printedAt: string; status?: string }[]; today: string; timeZone: string; roomName?: string; titleOf: (templateId: string) => string | undefined; applies?: (templateId: string) => boolean },
): PaperLine | undefined {
  const t = action?.templateId;
  if (!t) return undefined;
  const title = o.titleOf(t);
  if (!title || (o.applies && !o.applies(t))) return undefined;
  const line = `On paper? Use the ${title} sheet.`;
  const current = o.sheets.filter((x) => x.templateId === t && x.status !== "archived" && x.status !== "complete" && x.periodEnd >= o.today).sort((a, b) => b.printedAt.localeCompare(a.printedAt))[0];
  if (current) {
    let day: string;
    try { day = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: o.timeZone }).format(new Date(current.printedAt)); } catch { day = current.printedAt.slice(0, 10); }
    return { templateId: t, line, printed: { sheetId: current.id, code: current.code, line: `Printed ${day}.` } };
  }
  const room = o.roomName?.trim();
  return { templateId: t, line, print: { label: "Print it and stick it up", spot: room ? `Stick it up in the ${room.toLowerCase()}.` : "Stick it up where you work." } };
}

/**
 * Steps that carry their own rule, whatever item they sit in (so a wet floor sign is never shown as "Food Code 5-203.13").
 * Each citation read in the source: OSHA 1910.145(c)(2) caution signs (osha-housekeeping OS33, OS41 "Provide warning
 * signs for wet floor areas"); Food Code 6-501.16 "mops shall be placed in a position that allows them to air-dry".
 */
export const STEP_RULES: readonly { re: RegExp; citation: string }[] = [
  { re: /\bwet floor sign\b/i, citation: "OSHA 29 CFR 1910.145(c)(2)" },
  { re: /\bhang the mop\b.*\b(dry|air-dry)\b/i, citation: "Food Code 6-501.16" },
];
export const stepRuleOf = (say: string): string | undefined => STEP_RULES.find((r) => r.re.test(say))?.citation;

/* ─────────────── learn more: the library and the fridge diagram ─────────────── */

export interface StepLearn {
  topicId: string;
  title: string;
  /** Two or three lines from the topic: its summary, then its first lines. */
  excerpt: string[];
}

const TOPICS: [RegExp, string][] = [
  [/fridge order|top to bottom.*raw|raw poultry|ready-to-eat on top/i, "fridge-storage-order"],
  [/\bsanitiz|contact time|test strip|matching strip/i, "cleaning-sanitizing"],
  [/three-compartment|sink 1|sink 3/i, "three-compartment-sink"],
  [/cloth bucket|wiping cloth|sanitizer bucket/i, "wiping-cloths"],
  [/\b(dates?|expired|use-by|throw out)\b/i, "date-marking"],
  [/oldest in front|oldest to the front|\bFIFO\b/i, "dry-storage-fifo"],
  [/\bcool(ing)?\b.*\bfood\b/i, "cooling"],
  [/wash (your )?hands|handwash/i, "handwashing"],
  [/allerg|\bnut\b/i, "cross-contact"],
  [/dropping|gnaw|\bpests?\b|poison/i, "pest-signs"],
  [/\bmop\b/i, "restroom-mops-rags"],
  [/vomit|diarrh/i, "vomit-diarrhea-cleanup"],
  [/\b(chemical|cleaners?)\b.*\b(away|label|below)\b/i, "chemicals-sds"],
];

export function libraryExcerpt(topicId: string): StepLearn | undefined {
  const t = LIBRARY.find((x) => x.id === topicId);
  if (!t) return undefined;
  const first = (t.sections[0]?.body ?? "").split("\n").map((l) => l.replace(/^[-\d.]+\s*/, "").trim()).filter(Boolean).slice(0, 2);
  return { topicId: t.id, title: t.title, excerpt: [t.summary, ...first] };
}

/** The library topic for a step, by its words (the business library; homes have their own guides). */
export function libraryFor(...texts: (string | undefined)[]): StepLearn | undefined {
  for (const t of texts) {
    if (!t) continue;
    const hit = TOPICS.find(([re]) => re.test(t));
    if (hit) return libraryExcerpt(hit[1]);
  }
  return undefined;
}

export interface FridgeTier {
  n: number;
  label: string;
  /** The Food Code cooking temperature for the tier (none for food that won't be cooked). */
  cookF?: number;
  /** Drawn in a pan (raw meat sits in a pan or sealed container even on the right shelf). */
  inPan: boolean;
  examples: string[];
}
export interface FridgeDiagram {
  title: string;
  legend: string;
  tiers: FridgeTier[];
  note: string;
  citations: string[];
  topicId: "fridge-storage-order";
  /** The text-only list for screen readers and small screens. */
  text: string[];
}

/**
 * The example fridge (owner: "a rectangular food pyramid"), shelf by shelf, from library fridge-storage-order (Food
 * Code 3-302.11): higher shelf, lower cooking temperature. Examples are ordinary foods of each tier; produce is shown
 * washed, with the ready-to-eat food, since the source places no unwashed tier. Door shelves are left out: the source
 * doesn't place them.
 */
export const FRIDGE_ORDER: FridgeDiagram = {
  title: "Fridge order, top to bottom",
  legend: "Higher shelf = lower cooking temperature.",
  tiers: [
    { n: 1, label: "Food that won't be cooked", inPan: false, examples: ["Premade guacamole", "Salsa", "Cut fruit", "Washed produce", "Cooked leftovers", "Cooked meat", "Deli meat", "Cheese", "Milk and dairy", "Opened condiments", "Desserts", "Drinks"] },
    { n: 2, label: "Raw fish and seafood", cookF: 145, inPan: true, examples: ["Raw salmon", "Raw shrimp"] },
    { n: 3, label: "Raw whole beef, pork and lamb, and shell eggs", cookF: 145, inPan: true, examples: ["Steaks", "Roasts", "Pork chops", "Shell eggs"] },
    { n: 4, label: "Raw ground meat and ground fish", cookF: 155, inPan: true, examples: ["Ground beef", "Raw sausage", "Ground fish"] },
    { n: 5, label: "Raw poultry and stuffed foods", cookF: 165, inPan: true, examples: ["Raw chicken", "Raw turkey", "Stuffed pasta or meat"] },
  ],
  note: "Raw meat sits in a pan, even on the right shelf.",
  citations: ["Food Code 3-302.11", "Source protocols: refrigerator and cooler stacking order"],
  topicId: "fridge-storage-order",
  text: [],
};
FRIDGE_ORDER.text = FRIDGE_ORDER.tiers.map((t) => `Shelf ${t.n}${t.n === 1 ? " (top)" : t.n === FRIDGE_ORDER.tiers.length ? " (bottom)" : ""}: ${t.label}${t.cookF ? `, cooked to ${t.cookF}°F` : ""}. For example: ${t.examples.join(", ").toLowerCase()}.`);

/** Steps shown whole with a diagram (the splitter never breaks them up). Keyed by the plain step. */
export const STEP_DIAGRAMS: Record<string, "fridge_order"> = { "Put food back in fridge order.": "fridge_order" };

export const diagramOf = (key: "fridge_order"): FridgeDiagram => FRIDGE_ORDER;

export type { EasyIcon };
